import type { VenueSummary } from '@league/shared/contract';

import { Field, Input } from '../../components/ui';

/** A venue as `GET /api/markets/:id/venues` returns it. */
export type Venue = VenueSummary;

export interface VenueAddress {
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
}

/** `venueId` is '' for not decided yet, 'new' for one she is adding, or a venue's id. */
export interface VenueChoice {
  venueId: string;
  name: string;
  address: VenueAddress;
}

export const NO_VENUE: VenueChoice = {
  venueId: '',
  name: '',
  address: { addressLine1: '', city: '', state: '', postalCode: '' },
};

/** Whether the chosen venue still needs an address typed — a new one, or one saved without. */
function needsAddress(choice: VenueChoice, venues: Venue[]): boolean {
  if (choice.venueId === 'new') return choice.name.trim() !== '';
  const venue = venues.find((v) => v.id === choice.venueId);
  return Boolean(venue && !venue.addressLine1);
}

function addressComplete(address: VenueAddress): boolean {
  return address.addressLine1.trim() !== '' && address.city.trim() !== '' && address.state.trim().length === 2;
}

/**
 * A VENUE THE LEAGUE NAMES HAS A STREET ADDRESS (Courtney, 2026-10-12). Not decided yet is still
 * allowed; anything else is complete only with an address.
 */
export function venueChoiceComplete(choice: VenueChoice, venues: Venue[]): boolean {
  return !needsAddress(choice, venues) || addressComplete(choice.address);
}

/** What the API takes for this choice — see `resolveVenue` in the league routes. */
export function venuePayload(choice: VenueChoice, venues: Venue[]): Record<string, unknown> {
  const address = { ...choice.address, state: choice.address.state.toUpperCase() };
  if (choice.venueId === 'new') {
    return choice.name.trim() ? { newVenue: { name: choice.name.trim(), ...address } } : {};
  }
  if (!choice.venueId) return {};
  return needsAddress(choice, venues) ? { venueId: choice.venueId, venueAddress: address } : { venueId: choice.venueId };
}

/**
 * Pick a venue in the city, add one, or leave it undecided — and type the street address whenever
 * the venue named does not have one yet. Shared by the New league page and Settings.
 *
 * `pick` is false while the city itself is new, when there are no venues to choose from: the name
 * field is all there is, as before.
 */
export function VenueChooser({
  idPrefix,
  venues,
  pick,
  choice,
  defaultCity,
  defaultState,
  venueHint,
  nameHint,
  onChange,
}: {
  idPrefix: string;
  venues: Venue[];
  pick: boolean;
  choice: VenueChoice;
  defaultCity: string;
  defaultState: string;
  venueHint?: string;
  nameHint?: string;
  onChange: (choice: VenueChoice) => void;
}) {
  /* The city and state start as the league's own, because that is almost always where it plays. */
  const choose = (venueId: string) =>
    onChange({
      venueId,
      name: '',
      address: { addressLine1: '', city: defaultCity, state: defaultState, postalCode: '' },
    });
  const setAddress = (patch: Partial<VenueAddress>) => onChange({ ...choice, address: { ...choice.address, ...patch } });
  const chosen = venues.find((v) => v.id === choice.venueId);

  return (
    <div className="flex flex-col gap-4">
      {pick ? (
        <Field label="Venue" htmlFor={`${idPrefix}-venue`} hint={venueHint}>
          <select
            id={`${idPrefix}-venue`}
            value={choice.venueId}
            onChange={(e) => choose(e.target.value)}
            className="h-11 w-full rounded-md border border-control-border bg-paper px-3 text-[15px] focus-visible:border-deep"
          >
            <option value="">Not decided yet</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
            <option value="new">+ Somewhere new</option>
          </select>
        </Field>
      ) : null}

      {choice.venueId === 'new' ? (
        <Field label="Venue name" htmlFor={`${idPrefix}-venue-name`} hint={nameHint}>
          <Input
            id={`${idPrefix}-venue-name`}
            value={choice.name}
            onChange={(e) =>
              onChange({
                ...choice,
                name: e.target.value,
                address: {
                  ...choice.address,
                  city: choice.address.city || defaultCity,
                  state: choice.address.state || defaultState,
                },
              })
            }
          />
        </Field>
      ) : null}

      {chosen?.addressLine1 ? (
        <p className="text-[14px] text-ink-2">
          {chosen.addressLine1}, {chosen.city}, {chosen.state} {chosen.postalCode ?? ''}
        </p>
      ) : null}

      {needsAddress(choice, venues) ? (
        <div className="flex flex-col gap-3">
          <Field label="Street address" htmlFor={`${idPrefix}-street`}>
            <Input
              id={`${idPrefix}-street`}
              autoComplete="street-address"
              value={choice.address.addressLine1}
              onChange={(e) => setAddress({ addressLine1: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-[1fr_4.5rem_6.5rem] gap-3">
            <Field label="City" htmlFor={`${idPrefix}-venue-city`}>
              <Input
                id={`${idPrefix}-venue-city`}
                value={choice.address.city}
                onChange={(e) => setAddress({ city: e.target.value })}
              />
            </Field>
            <Field label="State" htmlFor={`${idPrefix}-venue-state`}>
              <Input
                id={`${idPrefix}-venue-state`}
                maxLength={2}
                value={choice.address.state}
                onChange={(e) => setAddress({ state: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="ZIP" htmlFor={`${idPrefix}-venue-zip`}>
              <Input
                id={`${idPrefix}-venue-zip`}
                inputMode="numeric"
                maxLength={10}
                className="tabular"
                value={choice.address.postalCode}
                onChange={(e) => setAddress({ postalCode: e.target.value })}
              />
            </Field>
          </div>
        </div>
      ) : null}
    </div>
  );
}
